/**
 * MultiLangFormRows.tsx — 运营后台多语言字段录入核心组件
 *
 * 移植自原型 design/Novel-admin/src/dialogs.jsx 的 window.__multiLang
 * （FormRow / OtherLangsField / useMultiLang / MultiLangFormRows）。
 *
 * 设计原则（与原型逐字一致）：中文 + 英文为必填行，其他语言可手动添加 / 删除，
 * 未维护时 C 端 fallback 英文。每行内嵌「从英文翻译」按钮——真调 DeepL（useI18nTranslate →
 * POST /api/v1/i18n/translate，源 en、目标该行语言；未配置 → toast 引导去集成页配 Key）。
 * 用于：分类名 / 标签名 / 书籍文案 / 推送模板 等**同一行承载多语言池**的字段（一处接全站生效）。
 *
 * ⚠️ 运营配置四模块（`home_sections` / `faq` / `shelf_recommendations` / `banner_slides`）**不是池模型**：
 * 它们按语言拆行，每行 i18n 池有且仅有本行 `language` 一个 key（dev-docs
 * `01-通用约定/85-运营配置维度模型.md` §5，DB CHECK `is_valid_scoped_title_i18n`），缺该语言 = 该语言
 * 无配置、**不回退英语**。这些调用方传 `requiredLangs={[lang]}` + `showOtherLangs={false}`，只录当前语言一行。
 *
 * 与原型差异（按 REBUILD-CONTRACT §2.4 冻结签名）：
 *   原型 useMultiLang 是内部 state hook + ml 对象传参；本组件改为受控
 *   （value: Record<string,string> + onChange(next)），由父组件持有状态。
 *   `multiline` 对应原型 type==='textarea'；`requiredLangs` 默认 ['zh','en']；
 *   `availableLangs` 对应原型 OtherLangsField 的可添加语言全集，默认 fallback
 *   静态 ['zh','en']（剔除必填后即「无其他语言」）；调用方需更多语言时传 availableLangs
 *   （经 useSysConfig 真数据：system_languages WHERE enabled 派生），不在组件内读 mock seed。
 * 视觉层（className / inline-style）与原型 1:1 保真，颜色用 var(--token)。
 */

import { type CSSProperties, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useI18nTranslate } from '~/hooks/use-i18n-translate';
import { langName } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { toast } from '~/ui/Toast';

/** 单条多语言映射：{ en:'...', zh:'...', de:'...' } */
export type MultiLangValue = Record<string, string>;

// =========================================================
// FormRow — 移植自 dialogs.jsx（左侧 140px 标签 + 右侧字段，底部分隔线）
// 多语言三行复用同一行布局，故内联于此组件文件。
// =========================================================
interface FormRowProps {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}
function FormRow({ label, hint, required, children }: FormRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        padding: '12px 0',
        borderBottom: '1px solid var(--border)',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ width: 140, flexShrink: 0, paddingTop: 6 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500 }}>
          {label}
          {required && <span style={{ color: 'var(--neg)', marginLeft: 3 }}>*</span>}
        </div>
        {hint && (
          <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 3, lineHeight: 1.45 }}>
            {hint}
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

// =========================================================
// 「其他语言」编辑区 — 移植自 dialogs.jsx 的 OtherLangsField
// values 的 key 存在 == 该语言已维护；不存在 == 未维护（可一键添加）
// =========================================================
export interface OtherLangsFieldProps {
  values: MultiLangValue; // { de: "..." } — key 存在即已维护
  enValue: string; // 当前英文值，供「从英文翻译」复制
  onChange: (code: string, value: string) => void;
  onAdd: (code: string) => void;
  onRemove: (code: string) => void;
  multiline: boolean;
  rows: number;
  availableLangs: string[]; // 可添加的其他语言全集（已剔除 zh/en）
  placeholderMap?: Record<string, string>; // 按 lang code 覆盖该行 placeholder（缺省走默认模板）
  placeholderTpl?: (zh: string) => string; // 其他语言行：从中文名派生 placeholder（缺省「<中文名>版本…」）
  /** 「从英文翻译」实现：(英文原文, 目标语言 code) → 译文。缺省 = useI18nTranslate（真 DeepL，源 en）；可注入桩用于测试 */
  onTranslate?: (text: string, targetCode: string) => Promise<string>;
  /** 翻译请求开始/结束通知；父级可据此阻止保存或离开，避免晚到译文丢失。 */
  onTranslatingChange?: (translating: boolean) => void;
}
export function OtherLangsField({
  values,
  enValue,
  onChange,
  onAdd,
  onRemove,
  multiline,
  rows,
  availableLangs,
  placeholderMap,
  placeholderTpl,
  onTranslate,
  onTranslatingChange,
}: OtherLangsFieldProps) {
  const { translateOne } = useI18nTranslate();
  const doTranslate = onTranslate ?? translateOne;
  // 逐行翻译态：仅当前翻译中的语言 code（其余行按钮不受影响）
  const [translatingCode, setTranslatingCode] = useState<string | null>(null);
  const runTranslate = async (code: string) => {
    if (!enValue || translatingCode) return;
    setTranslatingCode(code);
    onTranslatingChange?.(true);
    try {
      const translated = await doTranslate(enValue, code);
      onChange(code, translated);
    } catch (e) {
      // 未配置 DeepL（503 SERVICE_UNAVAILABLE）→ 引导配置（DeepL 凭证仅 admin+ 可配，故对 operator 用
      // 「联系管理员」中性措辞，不暗示当前用户可自助，避免死胡同）；其它失败 → 通用重试提示
      const notConfigured = e instanceof AdminApiError && e.code === 'SERVICE_UNAVAILABLE';
      toast(
        notConfigured ? 'DeepL 未配置，请联系管理员在 系统设置 → 集成 配置' : '翻译失败，请重试',
        { tone: 'neg', icon: 'IconAlert' },
      );
    } finally {
      setTranslatingCode(null);
      onTranslatingChange?.(false);
    }
  };
  // 语言中文名直接取 lib 的 langName —— 它的 SSOT 就是 SYS_CFG 的
  // system_languages.display_name_i18n.zh（sys-config 拉到即注入），无需再从 fmtLang 切字符串。
  // 其他语言行 placeholder：优先 placeholderTpl（从中文名派生），否则默认「<中文名>版本…」
  const tpl = (zh: string): string => (placeholderTpl ? placeholderTpl(zh) : `${zh}版本…`);
  const maintained = availableLangs.filter((code) => values[code] !== undefined);
  const unmaintained = availableLangs.filter((code) => values[code] === undefined);
  return (
    <div className="vstack" style={{ gap: 8 }}>
      {maintained.map((code) => {
        const zh = langName(code);
        // placeholderMap 命中该 lang 时优先用其覆盖文案，否则回退默认「<中文名>版本…」模板
        const ph = placeholderMap?.[code] ?? tpl(zh);
        const cellStyle: CSSProperties = {
          fontSize: 10.5,
          padding: '0 8px',
          height: 26,
          flexShrink: 0,
          marginTop: multiline ? 4 : 0,
        };
        return (
          <div
            key={code}
            className="hstack"
            style={{ gap: 8, alignItems: multiline ? 'flex-start' : 'center' }}
          >
            <span className="chip" style={cellStyle}>
              <span className="mono">{code.toUpperCase()}</span>
              <span style={{ color: 'var(--fg-faint)', marginLeft: 4 }}>{zh}</span>
            </span>
            {multiline ? (
              <textarea
                className="input"
                rows={rows}
                placeholder={ph}
                value={values[code]}
                disabled={translatingCode === code}
                onChange={(e) => onChange(code, e.target.value)}
                style={{ flex: 1, height: 'auto' }}
              />
            ) : (
              <input
                className="input"
                placeholder={ph}
                value={values[code]}
                disabled={translatingCode === code}
                onChange={(e) => onChange(code, e.target.value)}
                style={{ flex: 1, height: 26 }}
              />
            )}
            <button
              className="btn btn-ghost btn-sm"
              style={{ height: 26, flexShrink: 0 }}
              title={enValue ? `从英文翻译为${zh}` : '请先填写英文'}
              disabled={!enValue || translatingCode !== null}
              onClick={() => runTranslate(code)}
            >
              {translatingCode === code ? (
                <Icons.IconRefresh size={11} />
              ) : (
                <Icons.IconSparkles size={11} />
              )}
            </button>
            <button
              className="btn btn-ghost btn-icon"
              style={{ width: 26, height: 26, flexShrink: 0 }}
              disabled={translatingCode === code}
              onClick={() => onRemove(code)}
              title="移除该语言版本"
            >
              <Icons.IconX size={12} />
            </button>
          </div>
        );
      })}
      {unmaintained.length > 0 && (
        <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
          {unmaintained.map((code) => {
            const zh = langName(code);
            return (
              <button
                key={code}
                className="btn btn-sm"
                style={{ height: 24, padding: '0 8px' }}
                onClick={() => onAdd(code)}
                title={`添加 ${zh}版本`}
              >
                <Icons.IconPlus size={10} />
                <span className="mono">{code.toUpperCase()}</span>
                <span style={{ color: 'var(--fg-faint)', marginLeft: 3 }}>{zh}</span>
              </button>
            );
          })}
        </div>
      )}
      {maintained.length === availableLangs.length && availableLangs.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
          <Icons.IconCheck
            size={10}
            style={{ verticalAlign: 'middle', marginRight: 4, color: 'var(--pos)' }}
          />
          已维护全部 {availableLangs.length} 种其他语言
        </div>
      )}
      {availableLangs.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
          <Icons.IconInfo
            size={10}
            style={{ verticalAlign: 'middle', marginRight: 4, color: 'var(--info)' }}
          />
          当前 system_languages 仅含 en/zh 与禁用项· 其他语言启用后自动展示于此
        </div>
      )}
    </div>
  );
}

/** MultiLangFormRows 受控签名（REBUILD-CONTRACT §2.4 冻结） */
export interface MultiLangFormRowsProps {
  /** 字段名，用于行标签（如「标题」→「标题 (中文)」/「标题 (英文)」） */
  label?: string;
  /** 当前各语言取值：{ en:'...', zh:'...', de:'...' } */
  value: MultiLangValue;
  /** 任一语言改动后回调，传入完整 next 映射 */
  onChange: (next: MultiLangValue) => void;
  /** true → textarea；false/缺省 → input（对应原型 type==='textarea'） */
  multiline?: boolean;
  /** 必填语言行，默认 ['zh','en']（始终展示、不可删） */
  requiredLangs?: string[];
  /** 固定展示、不可删的语言行；默认与 requiredLangs 相同，可用于“常用但非必填”的语言 */
  pinnedLangs?: string[];
  /** 可添加的「其他语言」全集，默认取 system enabled languages */
  availableLangs?: string[];
  /** “其他语言”行的业务提示；缺省沿用全局英文 fallback 规则 */
  otherLangsHint?: string;
  /**
   * false → 整行「其他语言」不渲染。用于 per-language 分行的运营配置模块
   * （`banner_slides` / `faq` / `home_sections`，dev-docs `01-通用约定/85-运营配置维度模型.md` §5）：
   * 那些行的 i18n 池**有且仅有本行 `language` 一个 key**（DB CHECK `is_valid_scoped_title_i18n`），
   * 多录的语言既写不进库、也无处可存 —— 留着输入框等于给运营一个填了会被丢弃的死字段。
   */
  showOtherLangs?: boolean;
  /** 按 lang code 覆盖该行 placeholder（必填行与 OtherLangsField 行都透传）；缺省走默认模板 */
  placeholderMap?: Record<string, string>;
  /** 其他语言行：从中文名派生 placeholder（对应原型 placeholderTpl）；缺省「<中文名>版本…」 */
  placeholderTpl?: (zh: string) => string;
  /** true → 给第一条必填行的 input/textarea 挂 autoFocus（弹窗打开即聚焦）；缺省不聚焦 */
  autoFocusFirst?: boolean;
  /** 「从英文翻译」实现注入（缺省 = 真 DeepL via useI18nTranslate）；测试可注入桩 */
  onTranslate?: (text: string, targetCode: string) => Promise<string>;
  /** 其他语言翻译请求开始/结束通知。 */
  onTranslatingChange?: (translating: boolean) => void;
}

/**
 * 渲染「中文 / 英文 / 其他语言」三段式录入区（受控）。
 * 必填语言逐行渲染（不可删）；其余语言走 OtherLangsField，可加可删。
 */
export function MultiLangFormRows({
  label = '内容',
  value,
  onChange,
  multiline = false,
  requiredLangs = ['zh', 'en'],
  pinnedLangs = requiredLangs,
  availableLangs,
  otherLangsHint = '可选 · 未维护时 fallback 英文（统一的运营后台多语言规则）',
  showOtherLangs = true,
  placeholderMap,
  placeholderTpl,
  autoFocusFirst = false,
  onTranslate,
  onTranslatingChange,
}: MultiLangFormRowsProps) {
  const rows = 2;
  const [otherTranslationPending, setOtherTranslationPending] = useState(false);
  const handleTranslatingChange = (translating: boolean) => {
    setOtherTranslationPending(translating);
    onTranslatingChange?.(translating);
  };
  // 默认其他语言全集 fallback = 静态 ['zh','en']（剔除必填后即「无其他语言」，不读 mock seed）；
  // 调用方需更多语言时经 availableLangs 传入（useSysConfig 真数据：system_languages WHERE enabled 派生）。
  const otherLangs = (availableLangs ?? ['zh', 'en']).filter((code) => !pinnedLangs.includes(code));

  const setLang = (code: string, v: string) => onChange({ ...value, [code]: v });
  const addLang = (code: string) => {
    if (value[code] !== undefined) return;
    onChange({ ...value, [code]: '' });
  };
  const removeLang = (code: string) => {
    const next = { ...value };
    delete next[code];
    onChange(next);
  };

  // 必填语言行的标签后缀：一律取语言中文名（SSOT = system_languages.display_name_i18n.zh），
  // 不再对 zh / en 写死 —— 写死会与同屏其它行（走 langName）出现两种叫法。
  const reqLabel = (code: string): string => {
    const zh = langName(code);
    return zh ? `${label} (${zh})` : `${label} (${code.toUpperCase()})`;
  };

  return (
    <>
      {pinnedLangs.map((code, idx) => {
        // placeholderMap 命中该 lang 时优先用其覆盖文案，否则回退默认「<字段名>…」
        const ph = placeholderMap?.[code] ?? `${label}…`;
        // 仅第一条必填行在 autoFocusFirst 开启时聚焦（弹窗打开即落焦）
        const autoFocus = autoFocusFirst && idx === 0;
        return (
          <FormRow key={code} label={reqLabel(code)} required={requiredLangs.includes(code)}>
            {multiline ? (
              <textarea
                // 弹窗打开即聚焦第一条原因输入框（设计稿明确交互，noAutofocus 已在 biome 关闭）
                autoFocus={autoFocus}
                className="input"
                rows={rows}
                placeholder={ph}
                value={value[code] || ''}
                disabled={otherTranslationPending}
                onChange={(e) => setLang(code, e.target.value)}
                style={{ height: 'auto' }}
              />
            ) : (
              <input
                // 弹窗打开即聚焦第一条原因输入框（设计稿明确交互，noAutofocus 已在 biome 关闭）
                autoFocus={autoFocus}
                className="input"
                placeholder={ph}
                value={value[code] || ''}
                disabled={otherTranslationPending}
                onChange={(e) => setLang(code, e.target.value)}
              />
            )}
          </FormRow>
        );
      })}
      {showOtherLangs && (
        <FormRow label="其他语言" hint={otherLangsHint}>
          <fieldset
            disabled={otherTranslationPending}
            inert={otherTranslationPending}
            aria-busy={otherTranslationPending}
            style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
          >
            <OtherLangsField
              values={Object.fromEntries(
                Object.entries(value).filter(([k]) => !pinnedLangs.includes(k)),
              )}
              enValue={value.en || ''}
              onChange={setLang}
              onAdd={addLang}
              onRemove={removeLang}
              multiline={multiline}
              rows={rows}
              availableLangs={otherLangs}
              placeholderMap={placeholderMap}
              placeholderTpl={placeholderTpl}
              onTranslate={onTranslate}
              onTranslatingChange={handleTranslatingChange}
            />
          </fieldset>
        </FormRow>
      )}
    </>
  );
}
